import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Singles / Doubles / Teams pill toggle — used once, by TestDataStep.jsx
 * (standalone Explore mode's upfront "game type" question). Participant
 * type is never re-asked anywhere else in the wizard: real-tournament mode
 * sources it from the tournament's own registration type, and a format that
 * can't use 'teams' (e.g. ladder — see formatRegistry's `disallowsTeams`)
 * just forces the type back to 'singles' the moment that format is picked
 * (see useScheduleBuilder's `selectFormat`), rather than needing this
 * component to restrict which options are even choosable.
 *
 * `disabledOptions` is how TestDataStep disables Singles/Doubles for a
 * team-only sport (played_by === 'team' — cricket, soccer, volleyball,
 * basketball) — those sports have no singles/doubles form in real life.
 */
export const PARTICIPANT_COUNT_HINT = {
  singles: null,
  doubles: 'This is the number of pairs, not individual players — each pair counts as one.',
  teams: 'This is the number of teams, not individual players — each team fields its own roster.',
};

// The Test Data step's upfront question, per game type — teams asks for an
// exact count (it's the actual number of team rosters that will be built,
// not an estimate), while singles/doubles stay a rough starting point since
// real enrollment fluctuates.
export const PARTICIPANT_COUNT_QUESTION = {
  singles: 'Roughly how many participants?',
  doubles: 'Roughly how many teams of two?',
  teams: 'Exactly how many teams?',
};

const ParticipantTypeToggle = ({ value, onChange, disabledOptions = [] }) => {
  const { theme } = useTheme();
  return (
    <div className={`inline-flex rounded-full border ${theme.border} p-1`}>
      {['singles', 'doubles', 'teams'].map((opt) => {
        const isDisabled = disabledOptions.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(opt)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium capitalize transition-colors ${
              isDisabled
                ? `${theme.textSecondary} cursor-not-allowed opacity-40`
                : value === opt
                ? theme.buttonPrimary
                : `${theme.textSecondary} hover:${theme.text}`
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

export default ParticipantTypeToggle;
